// ConversationsSkeleton — content-shaped loading placeholders for /hub/conversations.
//
// A load should read as "already here", not as a stack of identical bars. These
// two skeletons mirror the real rail + reading pane layouts (the same trick
// shared/Skeleton's SkeletonDashboard uses for the analytics dashboards): section
// headers, channel/DM/thread-row shapes, the lens-tab strip, message rows, and
// the composer block — so the page assembles into its final shape as it loads
// instead of visibly repositioning when the data lands.
//
// Built on the shared <Skeleton> atom (shimmer + dark-mode safe) and carries
// role="status" / aria-live="polite" / an sr-only "Loading…" for assistive tech,
// matching SkeletonDashboard. Presentational only — never touches supabase.
import React from "react";
import { Skeleton } from "../../shared/Skeleton";

// A section header placeholder: a small uppercase label + a count pill.
const SkeletonHeader = () => (
  <div className="flex items-center gap-2 px-3 pb-1 pt-3">
    <Skeleton className="h-3 w-16" />
    <Skeleton className="h-3 w-5" />
  </div>
);

// A channel-row placeholder: border-l-4 transparent (the client-facing rail),
// a #name line, and a trailing count — same row geometry as ChannelRow.
const SkeletonChannelRow = () => (
  <div className="flex w-full items-center gap-2 rounded-lg border-l-4 border-l-transparent px-3 py-2">
    <Skeleton className="h-4 w-28" />
    <Skeleton className="ml-auto h-3 w-8" />
  </div>
);

// A DM-row placeholder: the presence dot + a name + a trailing count — same
// row geometry as DmRow.
const SkeletonDmRow = () => (
  <div className="flex w-full items-center gap-2 rounded-lg border-l-4 border-l-transparent px-3 py-2">
    <Skeleton className="h-2 w-2 shrink-0 rounded-full" />
    <Skeleton className="h-4 w-24" />
    <Skeleton className="ml-auto h-3 w-8" />
  </div>
);

// A thread-row placeholder: the SituationCard geometry — a rounded border card
// with a subject, a summary line, and a meta line. Mirrors ThreadRow's card so
// the threads queue reads as a queue while it loads.
const SkeletonThreadRow = () => (
  <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-800">
    <Skeleton className="h-4 w-2/3" />
    <Skeleton className="mt-2 h-3 w-full" />
    <Skeleton className="mt-1.5 h-3 w-1/3" />
  </div>
);

// ── Per-section row skeletons ────────────────────────────────────────────────
// The rail loads in three independent stages (channels → DMs → threads), so each
// section needs its own row-only loader — the section header (and the threads
// lens tabs / the channels quiet toggle) stay mounted while just the rows
// shimmer. This is what lets the page reveal channels first, then DMs, then
// threads, instead of blocking the whole rail on the heaviest fetch.
//
// `omitHeader` drops the SkeletonHeader for sections whose real header carries a
// control (the channels quiet toggle, the threads lens tabs) that must stay
// interactive while the rows load — so the control never flickers out.

export const ChannelsRowsSkeleton = ({ count = 3 }) => (
  <div role="status" aria-label="Loading channels" aria-live="polite">
    <ul className="space-y-0.5 px-2">
      {Array.from({ length: count }).map((_, i) => (
        <li key={`ch-${i}`}>
          <SkeletonChannelRow />
        </li>
      ))}
    </ul>
    <span className="sr-only">Loading channels…</span>
  </div>
);

export const DmsRowsSkeleton = ({ count = 2 }) => (
  <div role="status" aria-label="Loading direct messages" aria-live="polite">
    <ul className="space-y-0.5 px-2">
      {Array.from({ length: count }).map((_, i) => (
        <li key={`dm-${i}`}>
          <SkeletonDmRow />
        </li>
      ))}
    </ul>
    <span className="sr-only">Loading direct messages…</span>
  </div>
);

export const ThreadsRowsSkeleton = ({ count = 4 }) => (
  <div role="status" aria-label="Loading threads" aria-live="polite">
    <ul className="space-y-1.5 px-2 pt-1">
      {Array.from({ length: count }).map((_, i) => (
        <li key={`th-${i}`}>
          <SkeletonThreadRow />
        </li>
      ))}
    </ul>
    <span className="sr-only">Loading threads…</span>
  </div>
);

// The whole-rail skeleton — used only before the first section has resolved
// (the page swaps to the real rail the moment any section is ready). Composed
// from the row skeletons above so the shapes cannot drift apart.
export const ConversationsRailSkeleton = () => (
  <div className="space-y-1 px-1 pb-4" role="status" aria-label="Loading conversations" aria-live="polite">
    <SkeletonHeader />
    <ChannelsRowsSkeleton />

    <SkeletonHeader />
    <DmsRowsSkeleton />

    <SkeletonHeader />
    <div className="flex items-center gap-1 px-3 pb-1">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={`tab-${i}`} className="h-7 w-20 rounded-md" />
      ))}
    </div>
    <ThreadsRowsSkeleton />

    <span className="sr-only">Loading…</span>
  </div>
);

// A message-row placeholder — a from label + trailing timestamp, then a couple
// of body lines. Mirrors MessageRow so the stream reads as a stream.
const SkeletonMessageRow = () => (
  <div className="rounded-lg px-3 py-2.5">
    <div className="flex items-baseline gap-2">
      <Skeleton className="h-3 w-20" />
      <Skeleton className="ml-auto h-3 w-10" />
    </div>
    <Skeleton className="mt-2 h-5 w-full" />
    <Skeleton className="mt-1.5 h-5 w-4/5" />
  </div>
);

// The reading-pane skeleton — mirrors ThreadView: a header (subject + summary),
// a scrollable body of message rows, and the composer block at the bottom. Used
// both for the page-level initial load and for ThreadView's per-thread load, so
// switching threads never drops back to a row of identical bars.
export const ConversationsReadingSkeleton = () => (
  <div className="flex h-full flex-col" role="status" aria-label="Loading conversation" aria-live="polite">
    <header className="border-b border-gray-200 px-4 py-3 dark:border-gray-800">
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="mt-1.5 h-3 w-1/2" />
    </header>

    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
      <div className="space-y-1.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonMessageRow key={`msg-${i}`} />
        ))}
      </div>
    </div>

    <div className="shrink-0 border-t border-gray-200 p-3 dark:border-gray-800">
      <Skeleton className="h-24 w-full rounded-lg" />
      <div className="mt-2 flex justify-end">
        <Skeleton className="h-9 w-24 rounded-md" />
      </div>
    </div>

    <span className="sr-only">Loading…</span>
  </div>
);

export default Skeleton;
